import React, { useEffect, useState } from "react";

/**
 * Serves images with WebP format using <picture> element with fallback.
 *
 * Automatically generates a WebP source path from the original image path
 * (e.g., /images/logo.png → /images/logo.webp).
 *
 * Usage:
 *   <OptimizedImage src="/images/logo.png" alt="Logo" width={200} height={60} />
 */
const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className,
  style,
  loading = "lazy",
  sizes,
  fallbackSrcSet,
  webpSrcSet,
  decoding = "async",
  ...rest
}) => {
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    setUseFallback(false);
  }, [src]);

  if (!src) return null;

  // Generate WebP path from original
  const webpSrc = src.replace(/\.(png|jpe?g)$/i, ".webp");
  const isLocalImage = src.startsWith("/") || src.startsWith("./");
  const shouldUseWebp = process.env.NODE_ENV !== "development" && !useFallback;

  // Only use <picture> for local images (not Azure Blob URLs)
  if (!isLocalImage || webpSrc === src || !shouldUseWebp) {
    return (
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        style={style}
        loading={loading}
        decoding={decoding}
        sizes={sizes}
        srcSet={fallbackSrcSet}
        onError={() => setUseFallback(true)}
        {...rest}
      />
    );
  }

  return (
    <picture>
      <source srcSet={webpSrcSet || webpSrc} type="image/webp" sizes={sizes} />
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        style={style}
        loading={loading}
        decoding={decoding}
        sizes={sizes}
        srcSet={fallbackSrcSet}
        onError={() => setUseFallback(true)}
        {...rest}
      />
    </picture>
  );
};

export default OptimizedImage;
